import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper
} from '@mui/material';
import { ArrowBack, Send } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export const ContactSales: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Here you would typically send the data to your backend
    console.log('Sales inquiry submitted:', { email, company, message });
    
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  const handleBack = () => {
    navigate('/');
  };

  if (isSubmitted) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="md">
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: '#1a1a1a',
              border: '1px solid #2a2a2a'
            }}
          >
            <Typography variant="h3" component="h1" sx={{ color: 'white', mb: 3, fontWeight: 700 }}>
              Thank You!
            </Typography>
            <Typography variant="h6" sx={{ color: '#b0b0b0', mb: 4 }}>
              We've received your inquiry and our sales team will be in touch within 24 hours.
            </Typography>
            <Button
              variant="contained"
              onClick={handleBack}
              startIcon={<ArrowBack />}
              sx={{
                bgcolor: '#3a3a3a',
                color: 'white',
                px: 4,
                py: 1.5,
                '&:hover': { bgcolor: '#4a4a4a' }
              }}
            >
              Back to Homepage
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a' }}>
      {/* Header */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderBottom: 1,
          borderColor: '#1a1a1a',
          backdropFilter: 'blur(8px)',
          bgcolor: 'rgba(10, 10, 10, 0.9)'
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img
                src="/praytell_logo.png"
                alt="MyCatalyze"
                style={{ height: 32, width: 'auto' }}
              />
              <Typography variant="h6" component="h1" fontWeight="bold" sx={{ color: 'white' }}>
                MyCatalyze
              </Typography>
            </Box>
            
            <Button
              variant="text"
              onClick={handleBack}
              startIcon={<ArrowBack />}
              sx={{ 
                color: '#e0e0e0',
                '&:hover': { color: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              Back to Homepage
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Box sx={{ pt: 12, pb: 8 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                color: 'white',
                mb: 3
              }}
            >
              Let's Talk Enterprise
            </Typography>
            <Typography
              variant="h5"
              component="p"
              sx={{
                color: '#b0b0b0',
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Ready to transform your organization with AI-powered relationship intelligence? 
              Our enterprise team will help you build a custom solution.
            </Typography>
          </Box>

          <Paper
            sx={{
              p: 6,
              bgcolor: '#1a1a1a',
              border: '1px solid #2a2a2a'
            }}
          >
            <form onSubmit={handleSubmit}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                  Contact Information
                </Typography>
                <TextField
                  fullWidth
                  label="Work Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: '#3a3a3a' },
                      '&:hover fieldset': { borderColor: '#4a4a4a' },
                      '&.Mui-focused fieldset': { borderColor: '#3a3a3a' }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#b0b0b0',
                      '&.Mui-focused': { color: '#3a3a3a' }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Company Name"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  required
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: '#3a3a3a' },
                      '&:hover fieldset': { borderColor: '#4a4a4a' },
                      '&.Mui-focused fieldset': { borderColor: '#3a3a3a' }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#b0b0b0',
                      '&.Mui-focused': { color: '#3a3a3a' }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Tell us about your needs"
                  multiline
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe your use case, team size, and specific requirements..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: '#3a3a3a' },
                      '&:hover fieldset': { borderColor: '#4a4a4a' },
                      '&.Mui-focused fieldset': { borderColor: '#3a3a3a' }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#b0b0b0',
                      '&.Mui-focused': { color: '#3a3a3a' }
                    }
                  }}
                />
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  startIcon={<Send />}
                  sx={{
                    bgcolor: '#3a3a3a',
                    color: 'white',
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    '&:hover': { bgcolor: '#4a4a4a' },
                    '&:disabled': { bgcolor: '#2a2a2a', color: '#666666' }
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </Box>
            </form>
          </Paper>

          {/* Additional Info */}
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: 'white', mb: 3 }}>
              What happens next?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, justifyContent: 'center' }}>
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography variant="h4" sx={{ color: '#3a3a3a', mb: 1 }}>1</Typography>
                <Typography variant="body1" sx={{ color: '#b0b0b0' }}>
                  We'll review your requirements within 24 hours
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography variant="h4" sx={{ color: '#3a3a3a', mb: 1 }}>2</Typography>
                <Typography variant="body1" sx={{ color: '#b0b0b0' }}>
                  Schedule a personalized demo and consultation
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography variant="h4" sx={{ color: '#3a3a3a', mb: 1 }}>3</Typography>
                <Typography variant="body1" sx={{ color: '#b0b0b0' }}>
                  Receive a custom proposal and implementation plan
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
